import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import { fetchOrganizationFromNpd } from "../../utils/ndhLookup";
import LoadingIndicator from "../../utils/LoadingIndicator";
import NpdRecordCards from "../NpdRecordCards";
import classes from "./style.module.scss";

/**
 * Expanded view of an affiliated organization's own NPD record —
 * opened by clicking an organization row in NpdDetailsTab's
 * "Practices and affiliations" card. Wires up fetchOrganizationFromNpd()
 * from ndhLookup.js, which already existed (built for exactly this)
 * but had never actually been called from anywhere in the app.
 *
 * Renders through the same NpdRecordCards grid the practitioner tab
 * uses, so an organization's record looks and behaves identically —
 * not a second, hand-maintained copy of these cards. Care team is
 * hidden: fetchOrganizationFromNpd doesn't run the FHIR care-team
 * query (that's keyed off a practitioner's PractitionerRole), so
 * that card would always render empty here.
 */
export default function OrganizationDetailsModal({ open, orgNpi, onClose }) {
  const [status, setStatus] = useState("idle"); // idle | loading | success | not-found | error
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const cacheRef = useRef({}); // orgNpi -> parsed record, avoids re-hitting CMS on every reopen
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const load = () => {
    if (!orgNpi) return;
    if (cacheRef.current[orgNpi]) {
      setData(cacheRef.current[orgNpi]);
      setStatus("success");
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    fetchOrganizationFromNpd(orgNpi)
      .then((result) => {
        cacheRef.current[orgNpi] = result;
        setData(result);
        setStatus("success");
      })
      .catch((err) => {
        setStatus(err?.code === "NOT_FOUND" ? "not-found" : "error");
        setErrorMessage(err?.message || "Couldn't reach the provider directory.");
      });
  };

  useEffect(() => {
    if (open && orgNpi) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgNpi]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Grow}
      transitionDuration={prefersReducedMotion ? 0 : { enter: 280, exit: 180 }}
      TransitionProps={{
        easing: {
          enter: "cubic-bezier(0.16, 1, 0.3, 1)",
          exit: "cubic-bezier(0.4, 0, 1, 1)",
        },
      }}
      PaperProps={{
        className: classes["sheet"],
        sx: { maxHeight: "85vh", display: "flex", flexDirection: "column", margin: "16px" },
      }}
    >
      <IconButton aria-label="Close" onClick={onClose} size="small" className={classes["close-btn"]}>
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <div className={classes["header"]}>
        <span className={classes["header-icon"]}>
          <ApartmentRoundedIcon sx={{ fontSize: 15 }} />
        </span>
        <p className={classes["eyebrow"]}>Organization · NPD record</p>
      </div>

      <div className={classes["scroll-area"]}>
        {(status === "loading" || status === "idle") && (
          <div className={classes["state-block"]} role="status" aria-live="polite">
            <LoadingIndicator fullScreen={false} context="npd" height={72} />
          </div>
        )}

        {(status === "not-found" || status === "error") && (
          <div className={classes["state-block"]}>
            <p className={classes["state-text"]}>{errorMessage}</p>
            <button type="button" className={classes["retry-btn"]} onClick={load}>
              <RefreshRoundedIcon sx={{ fontSize: 15 }} />
              Try again
            </button>
            {orgNpi && (
              <a
                className={classes["fallback-link"]}
                href={`https://directory.cms.gov/organization/${orgNpi}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the NPD page instead
                <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
              </a>
            )}
          </div>
        )}

        {status === "success" && (
          <div className={classes["cards-pad"]}>
            <NpdRecordCards key={orgNpi} data={data} npi={orgNpi} showCareTeam={false} />
          </div>
        )}
      </div>
    </Dialog>
  );
}